import { Star } from 'lucide-react'
import { Suspense, lazy, useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { Link, useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom'

import type { OpMeta } from '@/catalog/types'
import { DataTable, type Column } from '@/components/data/DataTable'
import { detailActionClass } from '@/components/data/DetailPane'
import { LoadingRows, SearchInput } from '@/components/data/SearchInput'
import { StatusText } from '@/components/data/StatusText'
import { Page } from '@/components/layout/Page'
import { isFlagged, useOperationsStatus, type OpApiStatus } from '@/lib/apiCatalog'
import { fetchOps } from '@/lib/ops'
import { toggleFavorite, useOpPrefs } from '@/lib/opPrefs'
import { searchOps } from '@/lib/opSearch'
import { ClearFilters, Facet, FacetGroup } from '@/pages/Operations/Facets'
import { ModeSwitch } from '@/pages/Operations/ModeSwitch'
import { count, listParam, modeOf } from '@/pages/Operations/opsParams'

// Domains in the order admins look for them; the rest follow alphabetically.
const CAT_ORDER = ['fields', 'issues', 'users', 'projects', 'jsm', 'software', 'plans', 'webhooks', 'filters', 'screens', 'workflows', 'audits', 'governance']
const TYPES = ['read', 'write', 'critical'] as const
type OpType = (typeof TYPES)[number]
type FilterKey = 'q' | 'domain' | 'type'

const domainOf = (id: string) => id.split('.')[0]
const typeOf = (o: OpMeta): OpType => (o.critical ? 'critical' : !o.read_only ? 'write' : 'read')
const domainRank = (d: string) => {
  const i = CAT_ORDER.indexOf(d)
  return i < 0 ? CAT_ORDER.length : i
}
const byDomain = (a: string, b: string) => domainRank(a) - domainRank(b) || a.localeCompare(b)

function splitId(id: string): [string, string] {
  const i = id.indexOf('.')
  return i < 0 ? ['', id] : [id.slice(0, i + 1), id.slice(i + 1)]
}

// The Atlassian mode (catalog table, revision strip) stays out of the entry bundle.
const AtlassianCatalog = lazy(() => import('@/pages/Operations/AtlassianCatalog'))

/** Operations: our catalog by default, `?mode=atlassian` — the Atlassian API catalog. */
export default function OperationsList() {
  const [params] = useSearchParams()
  if (modeOf(params) !== 'atlassian') return <OurOperations />
  return (
    <Suspense fallback={<LoadingRows label="Loading the Atlassian API catalog" />}>
      <AtlassianCatalog />
    </Suspense>
  )
}

function OurOperations() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [ops, setOps] = useState<OpMeta[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  // the input is local state (instant typing); the URL follows it with replace navigation
  const [q, setQ] = useState(() => params.get('q') ?? '')
  const prefs = useOpPrefs()
  const apiStatus = useOperationsStatus()

  const load = useCallback(() => {
    setLoadError(false)
    setOps(null)
    fetchOps()
      .then((list) => {
        if (Array.isArray(list)) setOps(list)
        else setLoadError(true)
      })
      .catch(() => setLoadError(true))
  }, [])
  useEffect(load, [load])

  const setFilters = useCallback(
    (patch: Partial<Record<FilterKey, string>>) =>
      setParams((p) => {
        const next = new URLSearchParams(p)
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v)
          else next.delete(k)
        }
        return next
      }, { replace: true }),
    [setParams],
  )

  useEffect(() => {
    if ((params.get('q') ?? '') !== q) setFilters({ q })
    // only the typed query drives this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  // …and the input follows the URL when it changes from outside: the Operations menu item,
  // Back / Forward. Our own writes are REPLACE navigations and never echo back into q.
  const location = useLocation()
  const navType = useNavigationType()
  useEffect(() => {
    if (navType === 'REPLACE') return
    const urlQ = params.get('q') ?? ''
    if (urlQ !== q) setQ(urlQ)
    // runs once per navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  const query = q.trim()
  const known = useMemo(() => new Set((ops ?? []).map((o) => o.operation_id)), [ops])
  const allDomains = useMemo(
    () => [...new Set((ops ?? []).map((o) => domainOf(o.operation_id)))].sort(byDomain),
    [ops],
  )
  // values in the URL that this catalog doesn't have are ignored
  const domainSet = new Set(listParam(params, 'domain').filter((d) => allDomains.includes(d)))
  const typeSet = new Set(listParam(params, 'type').filter((t): t is OpType => (TYPES as readonly string[]).includes(t)))

  const searched = useMemo(() => {
    if (!ops) return []
    if (query) return searchOps(ops, query) // ranked, best first
    return [...ops].sort((a, b) =>
      byDomain(domainOf(a.operation_id), domainOf(b.operation_id)) || a.operation_id.localeCompare(b.operation_id))
  }, [ops, query])

  const inDomain = (o: OpMeta) => domainSet.size === 0 || domainSet.has(domainOf(o.operation_id))
  const inType = (o: OpMeta) => typeSet.size === 0 || typeSet.has(typeOf(o))
  const rows = searched.filter((o) => inDomain(o) && inType(o))

  // standard faceting: a group's counts honour the query and the other group's selection
  const domainCounts = new Map<string, number>()
  const typeCounts = new Map<OpType, number>()
  for (const o of searched) {
    if (inType(o)) count(domainCounts, domainOf(o.operation_id))
    if (inDomain(o)) count(typeCounts, typeOf(o))
  }

  const toggle = (key: 'domain' | 'type', current: Set<string>, v: string) => {
    const next = new Set(current)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    const order: readonly string[] = key === 'domain' ? allDomains : TYPES
    setFilters({ [key]: order.filter((x) => next.has(x)).join(',') })
  }
  const filtered = domainSet.size > 0 || typeSet.size > 0

  const open = (id: string) => navigate(`/operations/${id}`)

  const columns: Column<OpMeta>[] = [
    {
      key: 'fav', header: '★', width: '40px', title: () => undefined,
      render: (o) => <FavButton id={o.operation_id} fav={prefs.favorites.includes(o.operation_id)} inRow />,
    },
    {
      key: 'operation_id', header: 'Operation', mono: true, width: '34%',
      render: (o) => {
        const [ns, action] = splitId(o.operation_id)
        return <><span className="text-text-3">{ns}</span><span className="text-foreground">{action}</span></>
      },
    },
    { key: 'summary', header: 'Summary', render: (o) => <span className="text-text-2">{o.summary}</span> },
    {
      key: 'domain', header: 'Domain', width: '112px', title: () => undefined,
      render: (o) => <span className="text-text-2">{domainOf(o.operation_id)}</span>,
    },
    {
      key: 'type', header: 'Type', width: '84px', title: () => undefined,
      render: (o) => {
        const t = typeOf(o)
        return <span className={t === 'critical' ? 'font-medium text-danger' : 'text-text-2'}>{t}</span>
      },
    },
    {
      key: 'api', header: 'API', width: '104px',
      title: (o) => (isFlagged(apiStatus[o.operation_id]) ? apiStatus[o.operation_id].reasons.join('\n') : undefined),
      render: (o) => <ApiCell s={apiStatus[o.operation_id]} />,
    },
  ]

  // F on a focused row toggles its favorite (the row's star stays out of the tab order)
  const onTableKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement
    const id = el.tagName === 'TR' ? el.dataset.rowId : undefined
    if (!id || e.metaKey || e.ctrlKey || e.altKey || e.key.toLowerCase() !== 'f') return
    e.preventDefault()
    toggleFavorite(id)
  }

  return (
    <Page
      title="Operations"
      hint="Admin operations. Click a row or press Enter to open it; F on a row toggles its favorite."
      context={(
        <>
          <ModeSwitch mode="ours" />
          <SearchInput
            value={q}
            onChange={setQ}
            label="Search operations"
            placeholder="Search operations — words in any order"
            width="w-[360px]"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query && rows[0]) open(rows[0].operation_id)
            }}
          />
        </>
      )}
      freshness={ops ? <span className="font-mono tabular-nums">{rows.length} of {ops.length}</span> : undefined}
    >
      <div className="flex min-h-0 flex-1">
        <aside aria-label="Filters" className="w-[212px] shrink-0 overflow-y-auto border-r border-line py-1">
          {ops && (
            <>
              <FacetGroup title="Domain">
                {allDomains.map((d) => (
                  <Facet
                    key={d}
                    label={d}
                    mono
                    count={domainCounts.get(d) ?? 0}
                    active={domainSet.has(d)}
                    onClick={() => toggle('domain', domainSet, d)}
                  />
                ))}
              </FacetGroup>
              <FacetGroup title="Type">
                {TYPES.map((t) => (
                  <Facet
                    key={t}
                    label={t}
                    count={typeCounts.get(t) ?? 0}
                    active={typeSet.has(t)}
                    onClick={() => toggle('type', typeSet, t)}
                  />
                ))}
              </FacetGroup>
              {filtered && <ClearFilters onClick={() => setFilters({ domain: '', type: '' })} />}
            </>
          )}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {loadError
            ? (
              <p role="alert" className="flex items-center gap-3 border-b border-line px-4 py-2 text-[12.5px] text-danger">
                Couldn’t load the operations catalog.
                <button type="button" onClick={load} className={detailActionClass}>Retry</button>
              </p>
            )
            : ops === null
              ? <LoadingRows label="Loading operations" />
              : (
                <>
                  <PrefsStrip
                    favorites={prefs.favorites.filter((id) => known.has(id))}
                    recent={prefs.recent.filter((id) => known.has(id))}
                  />
                  <div className="flex min-h-0 flex-1 flex-col" onKeyDown={onTableKey}>
                    <DataTable
                      label="Operations"
                      rows={rows}
                      columns={columns}
                      rowId={(o) => o.operation_id}
                      onSelect={open}
                      empty={query ? `No operations match “${query}”.` : 'No operations match the filters.'}
                    />
                  </div>
                </>
              )}
        </div>
      </div>
    </Page>
  )
}

function ApiCell({ s }: { s: OpApiStatus | undefined }) {
  if (!s || !isFlagged(s)) return null
  return (
    <span data-testid="api-status">
      <StatusText status={s.status} />
    </span>
  )
}

function FavButton({ id, fav, inRow }: { id: string; fav: boolean; inRow?: boolean }) {
  return (
    <button
      type="button"
      // inside a table row: the click must not open the row; the row's F key covers the keyboard
      tabIndex={inRow ? -1 : undefined}
      onClick={(e) => {
        e.stopPropagation()
        toggleFavorite(id)
      }}
      aria-pressed={fav}
      aria-label={fav ? `Remove ${id} from favorites` : `Add ${id} to favorites`}
      title={fav ? 'Remove from favorites' : 'Add to favorites'}
      className={[
        'flex items-center justify-center rounded transition-colors hover:text-foreground',
        fav ? 'text-primary' : 'text-text-3',
        // in a row: no taller than the 30 px row
        inRow ? '-my-1 -ml-1 size-5' : 'size-6',
      ].join(' ')}
    >
      <Star className="size-3.5" fill={fav ? 'currentColor' : 'none'} aria-hidden />
    </button>
  )
}

/** Favorites and Recent (this browser) — one compact line each above the table. */
function PrefsStrip({ favorites, recent }: { favorites: string[]; recent: string[] }) {
  if (favorites.length === 0 && recent.length === 0) return null
  const lines = [['Favorites', favorites], ['Recent', recent]] as const
  return (
    <div role="region" aria-label="Recent and favorite operations" className="shrink-0 border-b border-line bg-pane px-4 py-1">
      {lines.map(([label, ids]) =>
        ids.length === 0 ? null : (
          <div key={label} className="flex min-h-[26px] flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="w-[72px] shrink-0 text-[11px] font-medium uppercase tracking-[0.06em] text-text-3">{label}</span>
            {ids.map((id) => (
              <span key={id} className="flex items-center">
                <Link
                  to={`/operations/${id}`}
                  className="rounded px-1 font-mono text-[12px] text-text-2 transition-colors hover:text-foreground"
                >
                  {id}
                </Link>
                {label === 'Favorites' && <FavButton id={id} fav />}
              </span>
            ))}
          </div>
        ),
      )}
    </div>
  )
}
